import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, RevealStagger, RevealItem } from "../components/Reveal";

const Services = () => {
  const { t } = useLang();
  const list = t("services.list");
  const stack = t("services.stack");
  const [hovered, setHovered] = useState(null);

  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="relative pt-32 md:pt-40 pb-12 md:pb-24 px-5 md:px-10">
        <div className="max-w-[1480px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-end">
            <div className="lg:col-span-8">
              <Reveal>
                <span className="tag">{t("services.kicker")}</span>
              </Reveal>
              <Reveal delay={0.1}>
                <h1 className="h-display mt-6 md:mt-8" data-testid="services-title">
                  {t("services.title_a")}
                  <span className="block ml-[4vw] md:ml-[6vw] text-[#E5FF00]">
                    {t("services.title_b")}
                  </span>
                </h1>
              </Reveal>
            </div>
            <div className="lg:col-span-4">
              <Reveal delay={0.2}>
                <p className="text-zinc-400 text-base md:text-lg leading-relaxed border-l border-white/15 pl-5 md:pl-6">
                  {t("services.lede")}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Service list */}
      <section className="px-5 md:px-10 pb-20 md:pb-24" data-testid="services-list-section">
        <div className="max-w-[1480px] mx-auto border-t border-white/15">
          <RevealStagger>
            {list.map((s, i) => (
              <RevealItem key={s.n}>
                <div
                  className="service-row border-b border-white/15 py-7 md:py-12 cursor-pointer"
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  data-testid={`service-row-${s.n}`}
                >
                  <div className="grid grid-cols-12 gap-3 md:gap-8 items-start">
                    {/* Number */}
                    <div className="col-span-12 md:col-span-1">
                      <span className="font-mono-tech text-[11px] md:text-sm text-[#E5FF00] uppercase tracking-[0.2em]">
                        {s.n}
                      </span>
                    </div>

                    {/* Title + tags */}
                    <div className="col-span-12 md:col-span-6">
                      <h3 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                        {s.title}
                      </h3>
                      <div className="flex flex-wrap gap-2 mt-4 md:mt-5">
                        {s.tags.map((tag) => (
                          <span
                            key={tag}
                            className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500 border border-white/10 px-2.5 py-1 md:px-3 md:py-1.5"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="col-span-12 md:col-span-4 mt-2 md:mt-2">
                      <p className="text-zinc-400 text-[15px] md:text-base leading-relaxed">
                        {s.desc}
                      </p>
                    </div>

                    {/* Arrow */}
                    <div className="col-span-12 md:col-span-1 hidden md:flex md:justify-end mt-3 md:mt-2">
                      <Plus
                        size={36}
                        strokeWidth={1.2}
                        className={`transition-all duration-500 ${
                          hovered === i
                            ? "text-[#E5FF00] rotate-45"
                            : "text-zinc-700"
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Tech stack */}
      <section className="px-5 md:px-10 py-20 md:py-32 border-t border-white/10" data-testid="services-tech-section">
        <div className="max-w-[1480px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mb-10 md:mb-14">
            <div className="lg:col-span-3">
              <Reveal>
                <span className="tag">{t("services.tech_kicker")}</span>
              </Reveal>
            </div>
            <div className="lg:col-span-8 lg:col-start-5">
              <Reveal delay={0.1}>
                <h2 className="h-display-md">{t("services.tech_title")}</h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="text-zinc-400 text-lg max-w-2xl mt-6">
                  {t("services.tech_lede")}
                </p>
              </Reveal>
            </div>
          </div>

          <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-white/10 border border-white/10">
            {stack.map((tech, i) => (
              <RevealItem key={tech} className="bg-[#0a0a0a] hover:bg-[#E5FF00] hover:text-black transition-colors duration-200 group">
                <div className="aspect-square flex flex-col items-center justify-center p-4 text-center">
                  <span className="font-display text-base md:text-xl tracking-tight">
                    {tech}
                  </span>
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600 group-hover:text-black/60 mt-2">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 md:px-10 py-20 md:py-24" data-testid="services-cta-section">
        <div className="max-w-[1480px] mx-auto">
          <div className="border border-white/10 p-7 md:p-20 bg-[#0a0a0a]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-end">
              <div className="lg:col-span-7">
                <span className="tag">{t("services.cta_kicker")}</span>
                <h2 className="h-display-md mt-5 md:mt-6">
                  {t("services.cta_title")}
                </h2>
              </div>
              <div className="lg:col-span-4 lg:col-start-9">
                <p className="text-zinc-400 mb-6">{t("services.cta_lede")}</p>
                <Link to="/contact" className="btn-acid" data-testid="services-cta-btn">
                  {t("nav.cta")}
                  <ArrowUpRight size={16} strokeWidth={2.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
